import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Heart, Users, Sparkles, Target, Compass, Star } from 'lucide-react';
import aboutData from '../content/about.json';

const iconMap = {
  Heart: Heart,
  Users: Users,
  Sparkles: Sparkles,
  Target: Target,
  Compass: Compass,
  Star: Star,
};

const AboutSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { sectionTitle, description, values: valuesData, approachTitle, approachParagraphs, tools, targetAudienceTitle, targetAudienceDescription } = aboutData;
  
  // CMS format: simple lists are arrays of objects
  const approachParagraphsArray = approachParagraphs ? approachParagraphs.map(p => p.paragraph || p) : [];
  const toolsArray = tools ? tools.map(t => t.tool || t) : [];

  const values = valuesData.map(value => ({
    ...value,
    icon: React.createElement(iconMap[value.icon] || Heart, { size: 32 })
  }));

  return (
    <section id="about" className="section-padding bg-gradient-to-b from-white to-nitzan-light-bg">
      <div className="container-width">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-gradient-to-r from-nitzan-mint/20 to-nitzan-teal/20 px-6 py-3 rounded-full mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <Star className="text-nitzan-teal" size={20} />
            <span className="text-nitzan-teal font-medium">אודותיי</span>
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">{sectionTitle}</span>
          </h2>
          
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            {description}
          </p>
        </motion.div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + index * 0.1, duration: 0.6 }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-nitzan-sage/20"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-nitzan-mint to-nitzan-teal flex items-center justify-center text-white">
                  {value.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-nitzan-teal mb-3">{value.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{value.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Main Story */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="bg-gradient-to-br from-nitzan-sage/10 via-nitzan-mint/10 to-nitzan-teal/10 rounded-3xl p-8 md:p-12 shadow-xl"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold text-nitzan-teal mb-6">{approachTitle}</h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                {approachParagraphsArray.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <motion.div
                className="bg-white rounded-2xl p-6 shadow-lg"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-4 mb-3">
                  <Target className="text-nitzan-teal" size={28} />
                  <h4 className="text-xl font-bold text-nitzan-teal">הכלים שלי</h4>
                </div>
                <ul className="space-y-2 text-gray-700">
                  {toolsArray.map((tool, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-nitzan-mint"></div>
                      <span>{tool}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                className="bg-gradient-to-br from-nitzan-teal to-nitzan-mint rounded-2xl p-6 shadow-lg text-white"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-4 mb-3">
                  <Users className="text-white" size={28} />
                  <h4 className="text-xl font-bold">למי זה מתאים?</h4>
                </div>
                <p className="leading-relaxed">
                  {targetAudienceDescription}
                </p>
              </motion.div>

            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="text-center mt-16"
        >
          <p className="text-xl text-gray-700 mb-6">מוכנ/ה להתחיל את המסע?</p>
          <Link to="/contact">
            <motion.div
              className="inline-block bg-gradient-to-r from-nitzan-teal to-nitzan-mint text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              בואי ניצור קשר
            </motion.div>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;

